import { useState } from 'react';
import {
    getCampaignThumbnail,
    getObjectiveImageFallback,
    isBootstrapOrEventGoalIcon,
} from '../helper/campaignDisplay';
import DefaultCampaignImage from '../assets/images/admin/social-img2.png';

const ICON_TILE_BG = 'bg-white border border-slate-200';

const EventIcon = ({ className = 'w-12 h-12' }) => (
    <svg
        viewBox="0 0 20 27"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.9272 2.87437C15.1152 1.04415 12.6495 0.00998664 10.0741 0H10C4.61522 0 0.25 4.36522 0.25 9.75V15C0.25 16.2426 1.25736 17.25 2.5 17.25H4C5.24264 17.25 6.25 16.2426 6.25 15V11.25C6.25 10.0074 5.24264 9 4 9H1.78375C2.17156 4.75177 5.7341 1.49979 10 1.5H10.0628C14.3107 1.51795 17.8457 4.76853 18.2191 9H16C14.7574 9 13.75 10.0074 13.75 11.25V15C13.75 16.2426 14.7574 17.25 16 17.25H17.5C18.7426 17.25 19.75 16.2426 19.75 15V9.75C19.7599 7.17412 18.7442 4.70022 16.9272 2.87437ZM4 10.5C4.41421 10.5 4.75 10.8358 4.75 11.25V15C4.75 15.4142 4.41421 15.75 4 15.75H2.5C2.08579 15.75 1.75 15.4142 1.75 15V10.5H4ZM18.25 15C18.25 15.4142 17.9142 15.75 17.5 15.75H16C15.5858 15.75 15.25 15.4142 15.25 15V11.25C15.25 10.8358 15.5858 10.5 16 10.5H18.25V15Z"
            fill="currentColor"
        />
    </svg>
);

const ObjectiveIconFallback = ({ goalMatch, iconClassName }) => {
    if (!goalMatch?.icon) return null;

    const imageClass = iconClassName || 'w-[72%] h-[72%] max-w-full max-h-full object-contain';

    if (goalMatch.icon.startsWith('bi')) {
        return (
            <i
                className={`${goalMatch.icon} text-theme_clr1 leading-none text-5xl`}
                title={goalMatch.name}
            />
        );
    }

    if (goalMatch.icon.startsWith('event')) {
        return <EventIcon className={`${imageClass} text-theme_clr1`} />;
    }

    return (
        <img
            src={goalMatch.icon}
            alt={goalMatch.name}
            className={imageClass}
        />
    );
};

const IconTile = ({ className, title, children }) => (
    <div
        className={`${className} ${ICON_TILE_BG} flex items-center justify-center overflow-hidden`}
        title={title}
    >
        {children}
    </div>
);

/**
 * Campaign thumbnail. Photos fill the tile; icons sit on a light background
 * so bluish assets stay visible.
 */
export const CampaignThumbnail = ({
    campaign,
    goalMatch,
    className = 'w-[120px] h-[120px] rounded-xl',
    iconClassName = 'w-[72%] h-[72%] max-w-full max-h-full object-contain',
}) => {
    const { src: primarySrc, isIcon: primaryIsIcon } = getCampaignThumbnail(campaign, goalMatch);
    const [failedSrc, setFailedSrc] = useState(null);

    const fallbackSrc = getObjectiveImageFallback(goalMatch) || DefaultCampaignImage;
    const src = failedSrc === primarySrc ? fallbackSrc : primarySrc;
    const isIcon = failedSrc === primarySrc ? true : primaryIsIcon;
    const showObjectiveIcon =
        failedSrc === primarySrc && isBootstrapOrEventGoalIcon(goalMatch?.icon);

    if (showObjectiveIcon) {
        return (
            <IconTile className={className} title={goalMatch?.name}>
                <ObjectiveIconFallback goalMatch={goalMatch} iconClassName={iconClassName} />
            </IconTile>
        );
    }

    if (isIcon) {
        return (
            <IconTile className={className} title={goalMatch?.name}>
                <img
                    src={src}
                    alt={goalMatch?.name || 'Campaign'}
                    className={iconClassName}
                    onError={() => {
                        if (failedSrc !== primarySrc) {
                            setFailedSrc(primarySrc);
                        }
                    }}
                />
            </IconTile>
        );
    }

    return (
        <img
            src={src}
            alt={goalMatch?.name || 'Campaign'}
            className={`${className} object-cover`}
            onError={() => {
                if (failedSrc !== primarySrc) {
                    setFailedSrc(primarySrc);
                }
            }}
        />
    );
};
